export function isSentryEnabled(): boolean {
	if (import.meta.env.DEV || (import.meta.env.VITE_SENTRY_DISABLE ?? 'false') === 'true') {
		return false;
	}

	return true;
}

export function isSentryFeedbackDevEnabled(): boolean {
	if (!import.meta.env.DEV) {
		return false;
	}

	return (import.meta.env.VITE_SENTRY_FEEDBACK_DEV ?? 'false') === 'true';
}

export function isSentryFeedbackEnabled(): boolean {
	return isSentryEnabled() || isSentryFeedbackDevEnabled();
}

export function isReactScanEnabled(): boolean {
	if (import.meta.env.DEV && (import.meta.env.VITE_REACT_SCAN_ENABLED ?? 'false') === 'true') {
		return true;
	}

	return false;
}

// GA4 measurement id, shared with the marketing site so app.elkmoney.com reports into the
// same property. Defaults to the production stream; override via VITE_GA_MEASUREMENT_ID (e.g.
// to point staging/dev at a separate property, or set it empty to disable the tag entirely).
export function getGaMeasurementId(): string {
	return import.meta.env.VITE_GA_MEASUREMENT_ID ?? 'G-DK6LB7EXXE';
}

/**
 * RevenueCat's Android **public** SDK key (`goog_…`), from the RevenueCat dashboard's Android app.
 *
 * Public by design — it ships inside the APK and can only read the current customer's own state.
 * The `sk_…` secret key must never appear here; it lives on the API and is what authoritatively
 * reads entitlements.
 *
 * Empty (the default) disables in-app purchases entirely, which is the correct state until the
 * RevenueCat project exists. Nothing else in the app breaks — see `getBillingPlatform()`.
 */
export function getRevenueCatAndroidApiKey(): string {
	return import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY ?? '';
}

/**
 * RevenueCat's Web Billing **public** SDK key (`rcb_…`), from the dashboard's Web Billing app.
 *
 * Usually a different key from the Android one: RevenueCat issues one per app in the project, and
 * the key is what tells it which store's products to serve. The Test Store is the exception — it
 * issues ONE key per project that every SDK shares, which is why local dev can put the same `test_…`
 * value in both vars. Public by the same reasoning as Android's — it ships in the JS bundle and can
 * only read the current customer's own state.
 *
 * Empty disables selling on the web, which is the correct state until Web Billing is configured
 * and a payment provider is connected in the dashboard.
 */
export function getRevenueCatWebApiKey(): string {
	return import.meta.env.VITE_REVENUECAT_WEB_API_KEY ?? '';
}

/**
 * Whether to put the RevenueCat SDK in DEBUG logging mode.
 *
 * Needs its own flag rather than riding on `import.meta.env.DEV`: an on-device test build is produced
 * by `vite build` (so DEV is false) and then copied in by `cap sync`, which is exactly the build where
 * these logs matter most. Without them, a failed purchase on a device gives you nothing to go on.
 *
 * DEV is still honoured, for `cap run android --livereload`.
 */
export function isRevenueCatDebugLoggingEnabled(): boolean {
	return import.meta.env.DEV || (import.meta.env.VITE_REVENUECAT_DEBUG_LOGS ?? 'false') === 'true';
}
